import { initializeApp } from "firebase/app";
import { createUserWithEmailAndPassword, getAuth } from "firebase/auth";
import {
  getFirestore,
  collection,
  addDoc,
  getDocs,
  getDoc,
  doc,
  setDoc,
  orderBy,
  query,
  where,
  updateDoc,
} from "firebase/firestore";
import { getDatabase, ref as databaseRef, set } from "firebase/database";
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import { HorseAd } from "../classes/HorseAd";
import User from "../classes/User";

const app = initializeApp({
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGINNG_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MESUREMENT_ID,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
});

export const auth = getAuth(app);
export const firestore = getFirestore(app);
export const database = getDatabase(app);
export const storage = getStorage(app);

export const ROOT_NODE: string = "HorseCollection";

export const writeDataToFirestore = async (path: string, data: any) => {
  try {
    const docRef = collection(firestore, path);
    const docId = docRef.id;
    data["id"] = docId;

    return await addDoc(docRef, data);
  } catch (error) {
    console.log("Error while writing data to firestore: ", error);
    return error;
  }
};

export const signUp = (email: string, password: string) => {
  return createUserWithEmailAndPassword(auth, email, password);
};

export const writeDataToFirestoreWithCustomID = async (
  path: string,
  id: string,
  data: any
) => {
  try {
    const docRef = doc(firestore, path, id);
    // const docId = docRef.id;
    data["id"] = id;

    return await setDoc(docRef, data);
  } catch (error) {
    console.log("Error while writing data to firestore 1: ", error);
    throw error;
  }
};

export const writeWelcomeEmailTrigger = async (userData: User) => {
  try {
    if (!userData.id || !userData.userId || !userData.userName) {
      throw new Error(
        "Invalid user data. Ensure id, email, and name are provided."
      );
    }

    // Reference to the Realtime Database path
    const dbRef = databaseRef(
      database,
      `/mailtriggers/welcomemail/${userData.id}`
    );

    // Write the data
    await set(dbRef, {
      email: userData.userId,
      name: userData.userName,
    });

    console.log("Welcome email trigger written successfully!");
  } catch (error) {
    console.error("Error writing welcome email trigger: ", error);
    throw error;
  }
};

export const writeNewCustomer = async (id: string, data: any) => {
  try {
    return await writeDataToFirestoreWithCustomID("Users", id, data);
  } catch (error) {
    console.log("Error while writing data to firestore 2: ", error);
    throw error;
  }
};

export const writeNewHorseAd = async (id: string, data: any) => {
  try {
    return await writeDataToFirestoreWithCustomID(ROOT_NODE, id, data);
  } catch (error) {
    console.log("Error while writing data to firestore: ", error);
    throw error;
  }
};

export const writeNewTicketToFB = async (
  projectId: string,
  id: string,
  data: any
) => {
  try {
    const path = ROOT_NODE + "/" + projectId + "/Tickets/";
    data["id"] = id;

    return writeDataToFirestoreWithCustomID(path, id, data);
  } catch (error) {
    console.log("Error while writing ticket data to firestore: ", error);
    throw error;
  }
};

export const writeNewReleaseToFB = async (
  projectId: string,
  id: string,
  data: any
) => {
  try {
    const path = ROOT_NODE + "/" + projectId + "/Releases/";
    data["id"] = id;

    return writeDataToFirestoreWithCustomID(path, id, data);
  } catch (error) {
    console.log("Error while writing ticket data to firestore: ", error);
    throw error;
  }
};

export const getDocsFromFirestore = async (path: string) => {
  try {
    const docsRef = collection(firestore, path);

    return await getDocs(docsRef);
  } catch (error) {
    console.log("Error while reading data from firestore: ", error);
    throw error;
  }
};

export const getDocsFromFirestoreWithSort = async (
  path: string,
  parm: string,
  order: "desc" | "asc"
) => {
  try {
    const docsRef = collection(firestore, path);
    const q = query(docsRef, orderBy(parm, order));

    return await getDocs(q);
  } catch (error) {
    // console.log("Error while reading data from firestore: ", error);
    throw error;
  }
};

export const getDocsFromFirestoreWhereEquals = async (
  path: string,
  parm: string,
  value: string
) => {
  try {
    const docsRef = collection(firestore, path);
    const q = query(docsRef, where(parm, "==", value));

    return await getDocs(q);
  } catch (error) {
    // console.log("Error while reading data from firestore: ", error);
    throw error;
  }
};

export const getMyHorsesFromFirestoreWhereEquals = async (userId: string) => {
  const parm = "selleruserId";
  try {
    const docsRef = collection(firestore, ROOT_NODE);
    const q = query(
      docsRef,
      where(parm, "==", userId),
      where("status", "in", [0, 1, 3, 4])
    );

    return await getDocs(q);
  } catch (error) {
    // console.log("Error while reading data from firestore: ", error);
    throw error;
  }
};

export const deleteTheHorseAd = async (ad: HorseAd) => {
  try {
    const newDocRef = doc(firestore, ROOT_NODE + "/" + ad.id);

    return await updateDoc(newDocRef, { status: 2 });
  } catch (error) {
    throw error;
  }
};

export const updateTheHorseAd = async (updatedAd: HorseAd) => {
  try {
    const newDocRef = doc(firestore, ROOT_NODE + "/" + updatedAd.id);

    return await updateDoc(newDocRef, { ...updatedAd });
  } catch (error) {
    throw error;
  }
};

export const getSignleDocFromFirestore = async (path: string) => {
  try {
    const docsRef = doc(firestore, path);

    return await getDoc(docsRef);
  } catch (error) {
    console.log("Error while reading signle doc from firestore: ", error);
    throw error;
  }
};

export const getDocsFromFirestoreWithSortWithoutLimit = async (
  parm: string,
  order: "desc" | "asc"
) => {
  try {
    const docsRef = collection(firestore, ROOT_NODE);
    const q = query(docsRef, orderBy(parm, order));

    return await getDocs(q);
  } catch (error) {
    // console.log("Error while reading data from firestore: ", error);
    throw error;
  }
};

export const getAdById = async (id: string): Promise<HorseAd | null> => {
  try {
    const adRef = doc(firestore, ROOT_NODE, id);
    const adSnap = await getDoc(adRef);

    if (adSnap.exists()) {
      return {
        id: adSnap.id,
        ...(adSnap.data() as Omit<HorseAd, "id">),
      };
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error fetching ad by ID:", error);
    return null;
  }
};

export const getDocsFromFirestoreWithCondition = async (
  parm: string,
  order: "desc" | "asc"
) => {
  try {
    const docsRef = collection(firestore, ROOT_NODE);
    const q = query(
      docsRef,
      where("status", "==", 1) // Filter for documents with status equal to 1
      // orderBy(parm, order)
    );

    return await getDocs(q);
  } catch (error) {
    throw error;
  }
};

export const getUserData = async (sellerId: string) => {
  try {
    const userRootNode = "Users";
    const docsRef = doc(firestore, `${userRootNode}/${sellerId}`);

    return await getDoc(docsRef);
  } catch (error) {
    console.log("Error while reading signle doc from firestore: ", error);
    throw error;
  }
};

export const uploadTicketMedia = async (userId: string, mediaFiles: File[]) => {
  let downloadableUrls = [];

  // Upload files to Firebase Storage
  const uploadTasks = mediaFiles.map((file) => {
    const timestamp = Date.now();
    const storageRef = ref(
      storage,
      `ImageFolder/image/${userId}/${timestamp}-${file.name}`
    );
    const uploadTask = uploadBytesResumable(storageRef, file);
    return uploadTask;
  });

  try {
    // Get download URLs and save to Firestore after all uploads are complete
    const snapshots = await Promise.all(uploadTasks);

    const urls = await Promise.all(
      snapshots.map((snapshot) => getDownloadURL(snapshot.ref))
    );

    downloadableUrls.push(...urls);

    // Return the downloadableUrls array
    return downloadableUrls;
  } catch (error) {
    console.log("Error uploading media:", error);
    // Handle the error, you might want to throw it or handle it in a specific way
    throw error;
  }
};

export const uploadHorseMedia = async (userId: string, mediaFiles: File[]) => {
  let downloadableUrls = [];

  // Upload files to Firebase Storage
  const uploadTasks = mediaFiles.map((file) => {
    const timestamp = Date.now();
    const storageRef = ref(
      storage,
      `ImageFolder/image/${userId}/${timestamp}-${file.name}`
    );
    const uploadTask = uploadBytesResumable(storageRef, file);
    return uploadTask;
  });

  try {
    // Get download URLs and save to Firestore after all uploads are complete
    const snapshots = await Promise.all(uploadTasks);

    const urls = await Promise.all(
      snapshots.map((snapshot) => getDownloadURL(snapshot.ref))
    );

    downloadableUrls.push(...urls);

    // Return the downloadableUrls array
    return downloadableUrls;
  } catch (error) {
    console.log("Error uploading media:", error);
    // Handle the error, you might want to throw it or handle it in a specific way
    throw error;
  }
};

export const uploadReleaseMedia = async (
  userId: string,
  mediaFiles: File[]
) => {
  let downloadableUrls = [];

  // Upload files to Firebase Storage
  const uploadTasks = mediaFiles.map((file) => {
    const timestamp = Date.now();
    const storageRef = ref(
      storage,
      `ProfilePictures/${userId}/${timestamp}-${file.name}`
    );
    const uploadTask = uploadBytesResumable(storageRef, file);
    return uploadTask;
  });

  try {
    // Get download URLs and save to Firestore after all uploads are complete
    const snapshots = await Promise.all(uploadTasks);

    const urls = await Promise.all(
      snapshots.map((snapshot) => getDownloadURL(snapshot.ref))
    );

    downloadableUrls.push(...urls);

    // Return the downloadableUrls array
    return downloadableUrls;
  } catch (error) {
    console.error("Error uploading media:", error);
    // Handle the error, you might want to throw it or handle it in a specific way
    throw error;
  }
};

export default auth;
